/**
 * Promotional Popup JavaScript
 * Handles popup display logic, triggers, and user interactions
 */

class PromotionalPopup {
  constructor(popup) {
    this.popup = popup;
    this.popupId = popup.dataset.popupId;
    this.delay = parseInt(popup.dataset.delay, 10) * 1000;
    this.trigger = popup.dataset.trigger;
    this.frequency = popup.dataset.frequency;
    this.frequencyDays = parseInt(popup.dataset.frequencyDays, 10);

    this.overlay = popup.querySelector('.promotional-popup__overlay');
    this.closeBtn = popup.querySelector('.promotional-popup__close');
    this.codeBtn = popup.querySelector('.promotional-popup__code');

    this.storageKey = `promotional-popup-${this.popupId}`;
    this.isShown = false;
    this.exitIntentTriggered = false;

    this.init();
  }

  init() {
    // Check if we should show the popup based on frequency settings
    if (!this.shouldShowPopup()) {
      return;
    }

    // Bind event listeners
    this.bindEvents();

    // Initialize based on trigger type
    switch (this.trigger) {
      case 'scroll':
        this.initScrollTrigger();
        break;
      case 'exit':
        this.initExitIntentTrigger();
        break;
      case 'load':
      default:
        this.initLoadTrigger();
        break;
    }

    // Handle theme editor
    if (Shopify.designMode) {
      this.show();
    }
  }

  shouldShowPopup() {
    // In theme editor, always show
    if (Shopify.designMode) {
      return true;
    }

    switch (this.frequency) {
      case 'always':
        return true;

      case 'session':
        return !sessionStorage.getItem(this.storageKey);

      case 'days':
        const lastShown = localStorage.getItem(this.storageKey);
        if (!lastShown) return true;

        const daysSince = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60 * 24);
        return daysSince >= this.frequencyDays;

      default:
        return true;
    }
  }

  markAsShown() {
    switch (this.frequency) {
      case 'session':
        sessionStorage.setItem(this.storageKey, 'true');
        break;
      case 'days':
        localStorage.setItem(this.storageKey, Date.now().toString());
        break;
    }
  }

  bindEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.hide());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.hide());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isShown) {
        this.hide();
      }
    });

    // Copy code button
    if (this.codeBtn) {
      this.codeBtn.addEventListener('click', () => this.copyCode());
    }
  }

  initLoadTrigger() {
    setTimeout(() => {
      this.show();
    }, this.delay);
  }

  initScrollTrigger() {
    const scrollHandler = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

      if (scrollPercent >= 50 && !this.isShown) {
        setTimeout(() => {
          this.show();
        }, this.delay);
        window.removeEventListener('scroll', scrollHandler);
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
  }

  initExitIntentTrigger() {
    const exitHandler = (e) => {
      // Only trigger when mouse leaves from the top of the page
      if (e.clientY <= 0 && !this.exitIntentTriggered) {
        this.exitIntentTriggered = true;
        setTimeout(
          () => {
            this.show();
          },
          Math.min(this.delay, 500),
        ); // Cap delay at 500ms for exit intent
        document.removeEventListener('mouseleave', exitHandler);
      }
    };

    // Only attach exit intent on desktop
    if (window.innerWidth >= 750) {
      document.addEventListener('mouseleave', exitHandler);
    } else {
      // Fallback to scroll trigger on mobile
      this.initScrollTrigger();
    }
  }

  show() {
    if (this.isShown) return;

    this.isShown = true;
    this.popup.classList.add('is-active');
    this.popup.setAttribute('aria-hidden', 'false');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus management
    this.closeBtn?.focus();

    // Mark as shown for frequency tracking
    this.markAsShown();
  }

  hide() {
    if (!this.isShown) return;

    this.isShown = false;
    this.popup.classList.remove('is-active');
    this.popup.setAttribute('aria-hidden', 'true');

    // Restore body scroll
    document.body.style.overflow = '';
  }

  async copyCode() {
    const code = this.codeBtn.dataset.code;

    try {
      await navigator.clipboard.writeText(code);
      this.codeBtn.classList.add('is-copied');

      setTimeout(() => {
        this.codeBtn.classList.remove('is-copied');
      }, 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        this.codeBtn.classList.add('is-copied');

        setTimeout(() => {
          this.codeBtn.classList.remove('is-copied');
        }, 2000);
      } catch (e) {
        console.error('Failed to copy code:', e);
      }

      document.body.removeChild(textArea);
    }
  }
}

// Initialize all popups on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const popups = document.querySelectorAll('.promotional-popup');
  popups.forEach((popup) => new PromotionalPopup(popup));
});

// Theme editor section select/deselect handling
if (Shopify.designMode) {
  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector('.promotional-popup');
    if (popup) {
      popup.classList.add('is-active');
    }
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const popup = event.target.querySelector('.promotional-popup');
    if (popup) {
      popup.classList.remove('is-active');
    }
  });
}
